import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/entities/user/model/user.types';

export type AuthClient = 'dj' | 'guest';

interface AuthState {
  idToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  // Which Cognito app client issued the tokens — refresh must go through the same one
  authClient: AuthClient | null;
  user: User | null;
  _hydrated: boolean;
  setTokens: (t: { idToken: string; accessToken: string; refreshToken?: string; expiresIn: number; client?: AuthClient }) => void;
  setUser: (user: User) => void;
  clear: () => void;
  setHydrated: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      idToken: null,
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      authClient: null,
      user: null,
      _hydrated: false,

      setTokens: ({ idToken, accessToken, refreshToken, expiresIn, client }) =>
        set({
          idToken,
          accessToken,
          refreshToken: refreshToken ?? get().refreshToken,
          expiresAt: Date.now() + expiresIn * 1000,
          authClient: client ?? get().authClient,
        }),

      setUser: (user) => set({ user }),

      clear: () => set({ idToken: null, accessToken: null, refreshToken: null, expiresAt: null, authClient: null, user: null }),

      setHydrated: () => set({ _hydrated: true }),
    }),
    {
      name: 'vm-auth',
      // _hydrated is runtime-only — exclude it so it never gets written back to localStorage
      partialize: (state) => ({
        idToken: state.idToken,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
        authClient: state.authClient,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
    },
  ),
);
